import { useCallback, useEffect, useMemo, useState } from 'react';
import { RELEASE_NOTES, type ReleaseNote, type WhatsNewScope } from './notes.js';

/**
 * "Yenilikler" görüldü işareti (M17, ADR 0064): kişinin açtığı en yeni girişin `id`'si bu
 * tarayıcıda ya da bu gemi bilgisayarında saklanır. Hesaba bağlı değildir; depo kapalıysa rozet
 * kalabilir, pencere yine açılır.
 */
export const WHATS_NEW_STORAGE_KEY = 'felix.whatsNew.seen';

/** Uygulamanın gördüğü girişler (en yeni başta). */
export function notesFor(
  scope: WhatsNewScope,
  notes: readonly ReleaseNote[] = RELEASE_NOTES,
): ReleaseNote[] {
  return notes.filter((note) => note.scopes.includes(scope));
}

/**
 * Görülmemiş girişler: işaretten daha yeni olanlar. İşaret yoksa (yeni kurulum) ya da listede
 * yoksa (giriş kaldırılmış) yalnız en yeni giriş sayılır: eski geçmiş rozet yapmaz.
 */
export function unseenNotes(notes: readonly ReleaseNote[], seenId: string | null): ReleaseNote[] {
  const index = seenId === null ? -1 : notes.findIndex((note) => note.id === seenId);
  if (index === -1) return notes.slice(0, 1);
  return notes.slice(0, index);
}

/** Depo arayüzü (`localStorage` ile uyumlu); testte değiştirilir. */
export interface WhatsNewStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): WhatsNewStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Saklanan işaret; depo yoksa ya da okunamıyorsa `null`. */
export function readSeen(storage: WhatsNewStorage | null): string | null {
  try {
    return storage?.getItem(WHATS_NEW_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

/** İşareti yazar; depo kapalı ya da doluysa sessizce geçer (rozet sonraki açılışta yine görünür). */
export function markSeen(storage: WhatsNewStorage | null, id: string): void {
  try {
    storage?.setItem(WHATS_NEW_STORAGE_KEY, id);
  } catch {
    // Depo kapalı ya da dolu
  }
}

/** Aynı penceredeki öteki kullanımlar (kullanıcı menüsü, Settings kartı) aynı anda güncellenir. */
const listeners = new Set<(id: string) => void>();

export interface WhatsNewState {
  notes: ReleaseNote[];
  /** Görülmemiş girişlerin kimlikleri (pencerede "New" işareti). */
  unseenIds: ReadonlySet<string>;
  /** Görülmemiş giriş var mı (menüde ve Software kartında rozet). */
  unseen: boolean;
  opened: boolean;
  /** Pencereyi açar ve en yeni girişi görüldü sayar. */
  open: () => void;
  close: () => void;
}

/**
 * Yenilikler penceresinin durumu. Pencere kendiliğinden açılmaz (sahibin isteği: zorla açılan
 * pencere yok); açıldığında rozet kalkar, pencerede o anki "New" işaretleri kapanana kadar kalır.
 */
export function useWhatsNew(
  scope: WhatsNewScope,
  options: { storage?: WhatsNewStorage | null; notes?: readonly ReleaseNote[] } = {},
): WhatsNewState {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  const notes = useMemo(() => notesFor(scope, options.notes), [scope, options.notes]);
  const [seen, setSeen] = useState(() => readSeen(storage));
  const [opened, setOpened] = useState(false);
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    listeners.add(setSeen);
    return () => {
      listeners.delete(setSeen);
    };
  }, []);

  const pending = useMemo(() => unseenNotes(notes, seen), [notes, seen]);
  const latest = notes[0]?.id ?? null;

  const open = useCallback(() => {
    setShown(new Set(pending.map((note) => note.id)));
    setOpened(true);
    if (latest === null) return;
    for (const listener of listeners) listener(latest);
    setSeen(latest);
    markSeen(storage, latest);
  }, [pending, latest, storage]);
  const close = useCallback(() => setOpened(false), []);

  return {
    notes,
    unseenIds: opened ? shown : new Set(pending.map((note) => note.id)),
    unseen: pending.length > 0,
    opened,
    open,
    close,
  };
}
